import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Star, Eye, Trash2 } from 'lucide-react';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { Markdown } from '@/components/common/Markdown';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  useArticle,
  useUpdateArticle,
  useDeleteArticle,
} from '@/features/content/queries';
import { useAuth } from '@/features/auth/AuthProvider';
import { Editor } from '@/features/editor/Editor';
import { Comments } from '@/features/comments/Comments';
import { EditToolbar, ToggleChip } from '@/features/content/EditToolbar';
import { formatDate } from '@/lib/utils';

interface Draft {
  titulo: string;
  resumen: string;
  contentMd: string;
  destacado: boolean;
  publicado: boolean;
}

function ArticlePage() {
  const params = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useArticle(params.section, params.subsection, params.article);
  const { user } = useAuth();
  const update = useUpdateArticle();
  const remove = useDeleteArticle();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({
    titulo: '',
    resumen: '',
    contentMd: '',
    destacado: false,
    publicado: true,
  });

  useEffect(() => {
    if (data?.article) {
      setDraft({
        titulo: data.article.titulo,
        resumen: data.article.resumen ?? '',
        contentMd: data.article.contentMd,
        destacado: data.article.destacado,
        publicado: data.article.publicado,
      });
    }
  }, [data?.article]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-80" />
      </div>
    );
  }
  if (!data) return <div>Artículo no encontrado</div>;
  const { section, subsection, article } = data;
  const isAdmin = user?.rol === 'admin';

  const save = async () => {
    await update.mutateAsync({
      id: article.id,
      data: {
        titulo: draft.titulo,
        resumen: draft.resumen,
        contentMd: draft.contentMd,
        destacado: draft.destacado,
        publicado: draft.publicado,
      },
    });
    setEditing(false);
  };

  const cancel = () => {
    setDraft({
      titulo: article.titulo,
      resumen: article.resumen ?? '',
      contentMd: article.contentMd,
      destacado: article.destacado,
      publicado: article.publicado,
    });
    setEditing(false);
  };

  const del = async () => {
    if (!confirm(`¿Eliminar el artículo "${article.titulo}"? Esta acción no se puede deshacer.`))
      return;
    await remove.mutateAsync(article.id);
    navigate({
      to: '/seccion/$section/$subsection',
      params: { section: section.slug, subsection: subsection.slug },
    });
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: section.titulo, to: `/seccion/${section.slug}` },
          {
            label: subsection.titulo,
            to: `/seccion/${section.slug}/${subsection.slug}`,
          },
          { label: article.titulo },
        ]}
      />

      {isAdmin && (
        <EditToolbar
          editing={editing}
          onEdit={() => setEditing(true)}
          onSave={save}
          onCancel={cancel}
          saving={update.isPending}
          viewActions={
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={del}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Eliminar
            </Button>
          }
          editActions={
            <>
              <ToggleChip
                pressed={draft.destacado}
                onPressedChange={(v) => setDraft({ ...draft, destacado: v })}
                icon={Star}
              >
                Destacado
              </ToggleChip>
              <ToggleChip
                pressed={draft.publicado}
                onPressedChange={(v) => setDraft({ ...draft, publicado: v })}
                icon={Eye}
              >
                Publicado
              </ToggleChip>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive ml-auto"
                onClick={del}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Eliminar
              </Button>
            </>
          }
        />
      )}

      <header className="space-y-3 border-b pb-6">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="brand">{section.titulo}</Badge>
          <Badge variant="secondary">{subsection.titulo}</Badge>
          {!editing && article.destacado && (
            <Badge variant="success">
              <Star className="mr-1 h-3 w-3" /> Destacado
            </Badge>
          )}
          {!editing && !article.publicado && (
            <Badge variant="outline">Borrador</Badge>
          )}
        </div>

        {editing ? (
          <Input
            value={draft.titulo}
            onChange={(e) => setDraft({ ...draft, titulo: e.target.value })}
            placeholder="Título del artículo"
            className="h-auto border-0 bg-transparent px-0 text-3xl font-bold tracking-tight shadow-none focus-visible:ring-0"
          />
        ) : (
          <h1 className="text-3xl font-bold tracking-tight">{article.titulo}</h1>
        )}

        {editing ? (
          <Textarea
            value={draft.resumen}
            onChange={(e) => setDraft({ ...draft, resumen: e.target.value })}
            placeholder="Resumen breve (aparece en listas, dashboard y resultados de búsqueda)"
            rows={2}
            className="resize-none border-0 px-0 text-base shadow-none focus-visible:ring-0"
          />
        ) : (
          article.resumen && <p className="text-muted-foreground text-base">{article.resumen}</p>
        )}

        <div className="text-muted-foreground flex items-center gap-3 text-xs">
          {article.autor && <span>Por {article.autor.nombre}</span>}
          <span>· Actualizado el {formatDate(article.updatedAt)}</span>
          <span>· {article.vistas} vistas</span>
        </div>
      </header>

      {editing ? (
        <Editor
          value={draft.contentMd}
          onChange={(md) => setDraft({ ...draft, contentMd: md })}
          articleId={article.id}
        />
      ) : (
        <Markdown content={article.contentMd} />
      )}

      <Comments articleId={article.id} />
    </div>
  );
}

export const Route = createFileRoute('/seccion/$section_/$subsection_/$article')({
  component: ArticlePage,
});
